/**
 * 入力検証の共通部品（02 2.10）。画面・API・CSV 取込で同じものを使う（要件書 3.2・5.4）。
 * エラー時の message には仕様書 10 章のメッセージ ID を入れ、画面は辞書（messages）で文言に直す（03 3.6.1）。
 */
import { z } from 'zod';
import { isSystemCode } from '../codes/issue-system-code';
import { isValidJstDate } from '../date/jst';

const FULL_WIDTH_ALPHA_PATTERN = /[Ａ-Ｚａ-ｚ]/;

/** 業務コード・システムコードの受け口（D-015） */
export const zCode = z.string({ message: 'MSG-V-020' }).trim().min(1, 'MSG-V-020').max(50, 'MSG-V-020');

/** UUID v7 */
export const zSystemCode = z
  .string({ message: 'MSG-V-020' })
  .uuid('MSG-V-020')
  .refine((value) => isSystemCode(value), 'MSG-V-020');

/** 任意の文字（前後の空白を除き、空文字は null にする） */
export function zShortText(max_length: number) {
  return z.preprocess(
    (value) => {
      if (typeof value !== 'string') {
        return value;
      }
      const trimmed = value.trim();
      return trimmed === '' ? null : trimmed;
    },
    z.string({ message: 'MSG-V-020' }).max(max_length, 'MSG-V-005').nullable(),
  );
}

/** 必須の文字。message を渡すと未入力の文言を項目別にする（例 MSG-V-053） */
export function zRequiredText(max_length: number, required_message_id = 'MSG-V-020') {
  return z.string({ message: required_message_id }).trim().min(1, required_message_id).max(max_length, 'MSG-V-005');
}

/** 全角カナ（要件書 5.4「企業名カナ 全角カナ」）。未入力の文言は required_message_id（企業名カナは MSG-V-052） */
export function zKatakana(max_length: number, required_message_id = 'MSG-V-051') {
  return z
    .string({ message: required_message_id })
    .trim()
    .min(1, required_message_id)
    .max(max_length, 'MSG-V-005')
    .regex(/^[ァ-ヶー\u3000 ・（）()－]+$/, 'MSG-V-051');
}

export const zPostalCode = z.string({ message: 'MSG-V-098' }).regex(/^\d{3}-?\d{4}$/, 'MSG-V-098');

export const zPhone = z
  .string({ message: 'MSG-V-082' })
  .min(10, 'MSG-V-082')
  .max(20, 'MSG-V-082')
  .regex(/^[0-9\-+()]+$/, 'MSG-V-082');

/** メールアドレス（小文字に正規化） */
export const zEmail = z
  .string({ message: 'MSG-V-073' })
  .trim()
  .toLowerCase()
  .max(254, 'MSG-V-073')
  .email('MSG-V-073')
  .refine((value) => !FULL_WIDTH_ALPHA_PATTERN.test(value), 'MSG-V-074');

/** URL。全角英字は MSG-V-074（02 2.10 zNoFullWidthAlpha を併用。URL の解析は全角の英字のホスト名を半角に直して通すため先に見る） */
export const zUrl = z
  .string({ message: 'MSG-V-072' })
  .trim()
  .regex(/^[^Ａ-Ｚａ-ｚ]*$/, 'MSG-V-074')
  .max(2000, 'MSG-V-072')
  .url('MSG-V-072')
  .refine((value) => /^https?:/.test(value), 'MSG-V-072');

export const zInvoiceNumber = z.string({ message: 'MSG-V-016' }).regex(/^T\d{13}$/, 'MSG-V-016');
export const zCorporateNumber = z.string({ message: 'MSG-V-090' }).regex(/^\d{13}$/, 'MSG-V-090');
export const zMyNumber = z.string({ message: 'MSG-V-036' }).regex(/^\d{12}$/, 'MSG-V-036');
export const zBankCode = z.string({ message: 'MSG-V-055' }).regex(/^\d{4}$/, 'MSG-V-055');
export const zBranchCode = z.string({ message: 'MSG-V-063' }).regex(/^\d{3}$/, 'MSG-V-063');
export const zAccountNumber = z.string({ message: 'MSG-V-060' }).regex(/^\d{1,7}$/, 'MSG-V-060');

/** 金額（0 以上・整数 13 桁・小数 2 桁）。Decimal 文字列 */
export const zMoney = z.string({ message: 'MSG-V-005' }).regex(/^\d{1,13}(\.\d{1,2})?$/, 'MSG-V-005');
/** 負数を許す金額（目標値・現在値だけ。D-016） */
export const zSignedMoney = z.string({ message: 'MSG-V-005' }).regex(/^-?\d{1,13}(\.\d{1,2})?$/, 'MSG-V-005');
/** 稼働時間（D-017） */
export const zHours = z.string({ message: 'MSG-V-005' }).regex(/^\d{1,3}(\.\d{1,2})?$/, 'MSG-V-005');
/** 確度・進捗率（D-018） */
export const zPercent = z.coerce
  .number({ message: 'MSG-V-005' })
  .int('MSG-V-005')
  .min(0, 'MSG-V-005')
  .max(100, 'MSG-V-005');

export const zDate = z
  .string({ message: 'MSG-V-020' })
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'MSG-V-020')
  .refine((value) => isValidJstDate(value), 'MSG-V-020');

/** UTC の Z 付き日時 */
export const zDateTime = z.string({ message: 'MSG-V-020' }).datetime({ offset: false, message: 'MSG-V-020' });

export const zYearMonth = z.string({ message: 'MSG-V-020' }).regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'MSG-V-020');

/** 締め日・支払日（31＝月末） */
export const zDayOfMonth = z.coerce
  .number({ message: 'MSG-V-005' })
  .int('MSG-V-005')
  .min(1, 'MSG-V-005')
  .max(31, 'MSG-V-005');

/** 全角英字を含まない（メール・URL・コード類に併用） */
export const zNoFullWidthAlpha = z.string().refine((value) => !FULL_WIDTH_ALPHA_PATTERN.test(value), 'MSG-V-074');

export const zVersion = z.number({ message: 'MSG-V-020' }).int('MSG-V-020').min(1, 'MSG-V-020');

/** 空文字（前後の空白だけを含む）を null にしてから検証する任意項目 */
export function zNullable<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess((value) => (typeof value === 'string' && value.trim() === '' ? null : value), schema.nullable());
}

/** 区分値の z.enum（02 6.4） */
export function zEnum<const T extends readonly [string, ...string[]]>(values: T) {
  return z.enum(values as unknown as [T[number], ...T[number][]], { message: 'MSG-V-020' });
}
